/* eslint-disable no-restricted-globals */
import { useState, useRef, useEffect } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Icon, Snackbar, SnackbarCloseReason, Typography, LinearProgress, Divider, Paper, alpha, useTheme } from '@mui/material';
import { VTextFieldData, IVFormErrors, VForm, useVForm, SeletorFormasPagamento, IFormaPagamentoSelecionada, OPERACOES_PAGAMENTO_FATURAMENTO } from '../../../shared/forms';
import * as yup from 'yup';
import dayjs from 'dayjs';
import { FaturamentoService } from '../../../shared/services/api/Financeiro/Faturamento';
import { FullScreenLoading } from '../../../shared/components';

interface IFormData {
  idmov: string;
  datapgto: string;
}
export interface ConfirmationDialogRawProps {
  id: string | null;
  open: boolean;
  parceiroNome: string;
  valorTotal: number;
  // Conta da sessão de caixa ativa do usuário — Faturamento só abre esse
  // dialog com uma sessão aberta, então sempre vem preenchida (ver
  // Faturamento.tsx, que bloqueia a tela sem sessão).
  idContaSessao: string;
  // Só pra exibição no resumo — opcionais porque nem toda tela que abre
  // esse dialog tem esses dados à mão.
  numeroVenda?: number | string;
  cpfCliente?: string;
  onClose: (value?: string) => void;
}
const formValidationSchema: yup.ObjectSchema<Omit<IFormData, 'idmov'>> =
  yup.object().shape({
    datapgto: yup.string().required('Data é obrigatória'),
  });

export const DialogFaturar: React.FC<ConfirmationDialogRawProps> = (props: ConfirmationDialogRawProps) => {
  const { onClose, open } = props;
  const theme = useTheme();
  const { formRef, save, } = useVForm();
  const [isLoading, setIsLoading] = useState(false);
  const [showFullScreenLoading, setShowFullScreenLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Faturando...');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const requestInProgress = useRef(false);
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');
  const [formasPagamentoSelecionadas, setFormasPagamentoSelecionadas] = useState<IFormaPagamentoSelecionada[]>([]);

  useEffect(() => {
    if (!open) {
      setFormasPagamentoSelecionadas([]);
    }
  }, [open]);

  const somaFormas = formasPagamentoSelecionadas.reduce((acc, f) => acc + f.valor, 0);
  const restante = Math.max(0, Number((props.valorTotal - somaFormas).toFixed(2)));
  const percentualPago = props.valorTotal > 0 ? Math.min(100, (somaFormas / props.valorTotal) * 100) : 0;
  const formatarMoeda = (valor: number) => valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  // F12 confirma, Esc cancela — Enter só confirma fora de campos de texto
  // (dentro do campo Valor do seletor de formas, Enter já adiciona a forma,
  // ver SeletorFormasPagamento).
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (showFullScreenLoading) return;
      if (event.key === 'F12') {
        event.preventDefault();
        save();
        return;
      }
      if (event.key === 'Escape') {
        handleCancel();
        return;
      }
      const alvo = event.target as HTMLElement | null;
      const digitando = alvo && ['INPUT', 'TEXTAREA'].includes(alvo.tagName);
      if (event.key === 'Enter' && !digitando) {
        event.preventDefault();
        save();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, showFullScreenLoading]);

  const simulateProgress = () => {
    setLoadingProgress(0);
    const interval = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 90) {
          clearInterval(interval);
          return 90;
        }
        return prev + 10;
      });
    }, 500);
    return () => clearInterval(interval);
  };

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
    requestInProgress.current = false;
    if (tms === 'success') {
      onClose();
    }
  };

  const handleSave = (dados: IFormData) => {
    if (requestInProgress.current) {
      return;
    }
    requestInProgress.current = true;
    let progressCleanup: (() => void) | null = null;
    formValidationSchema.validate(dados, { abortEarly: false })
      .then((dadosValidados) => {
        if (formasPagamentoSelecionadas.length === 0) {
          requestInProgress.current = false;
          setTms('error');
          setMsg('Selecione ao menos uma forma de pagamento');
          setOpenmsg(true);
          return;
        }
        if (Math.abs(somaFormas - props.valorTotal) > 0.01) {
          requestInProgress.current = false;
          setTms('error');
          setMsg(`Soma das formas de pagamento (${somaFormas.toFixed(2)}) não confere com o total (${props.valorTotal.toFixed(2)})`);
          setOpenmsg(true);
          return;
        }

        setIsLoading(true);
        setShowFullScreenLoading(true);
        setLoadingMessage('Validando dados...');
        progressCleanup = simulateProgress();

        setLoadingMessage('Processando faturamento...');
        setLoadingProgress(40);

        const dadosEnvio = {
          idmov: props.id ?? '',
          idconta: props.idContaSessao,
          datapgto: dayjs(dadosValidados.datapgto).format('YYYY-MM-DD'),
          formasPagamento: formasPagamentoSelecionadas.map((f) => ({
            idoperacao: f.idoperacao,
            valor: f.valor,
            categoria: f.nome,
            tband: f.tband,
            caut: f.caut,
            idcontadestino: f.idcontadestino,
            datacompensacao: f.datacompensacao,
          })),
        };

        setLoadingMessage('Finalizando lançamento...');
        setLoadingProgress(70);

        return FaturamentoService
          .createfaturar(dadosEnvio)
          .then((result) => {
            setLoadingProgress(100);
            setLoadingMessage('Faturamento concluído!');

            setTimeout(() => {
              setIsLoading(false);
              setShowFullScreenLoading(false);

              if (result instanceof Error) {
                requestInProgress.current = false;

                setTms('error');
                setMsg(result.message);
                setOpenmsg(true);

              } else {
                setTms('success');
                setMsg('Lançamento Realizado com sucesso!');
                setOpenmsg(true);
              }

              if (progressCleanup) {
                progressCleanup();
              }
            }, 500);
          });
      })
      .catch((errors: yup.ValidationError) => {

        requestInProgress.current = false;

        setIsLoading(false);
        setShowFullScreenLoading(false);

        if (progressCleanup) {
          progressCleanup();
        }

        const validationErrors: IVFormErrors = {};

        errors.inner.forEach(error => {
          if (!error.path) return;
          validationErrors[error.path] = error.message;
        });

        formRef.current?.setErrors(validationErrors);

        setTms('error');
        setMsg('Erro de validação. Verifique os campos.');
        setOpenmsg(true);
      })
      .catch((error) => {

        requestInProgress.current = false;

        setIsLoading(false);
        setShowFullScreenLoading(false);

        if (progressCleanup) {
          progressCleanup();
        }

        setTms('error');
        setMsg(error.message || 'Erro ao processar faturamento');
        setOpenmsg(true);
      });
  };

  const handleCancel = () => {
    if (showFullScreenLoading) {
      return;
    }
    onClose();
  };

  return (
    <>
      {showFullScreenLoading && (
        <FullScreenLoading
          message={loadingMessage}
          progress={loadingProgress}
        />
      )}
      <Dialog
        open={open}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <Icon color="primary">payments</Icon>
          <Typography fontWeight={700} fontSize="1.1rem">
            Faturar venda
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 2.5 }}>
          <VForm ref={formRef} onSubmit={handleSave} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <Snackbar
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              open={openmsg}
              autoHideDuration={tms === 'error' ? null : 2000}
              onClose={handleCloseMsg}
            >
              <Alert
                onClose={handleCloseMsg}
                severity={tms}
                variant="filled"
                sx={{ width: '100%' }}
              >
                {msg}
              </Alert>
            </Snackbar>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <VTextFieldData
                  fullWidth
                  name='datapgto'
                  label='Data Pagamento'
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Formas de pagamento
            </Typography>

            {/* Mesma divisão da dialog de Finalizar Venda do PDV: lista de
                formas | valor + pagamentos adicionados | resumo. */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 2, alignItems: 'flex-start' }}>
              <Box sx={{ flex: '1 1 auto', minWidth: 0, width: '100%' }}>
                <SeletorFormasPagamento
                  key={open ? 'aberto' : 'fechado'}
                  total={props.valorTotal}
                  formas={formasPagamentoSelecionadas}
                  onChange={setFormasPagamentoSelecionadas}
                  disabled={isLoading}
                  operacoes={OPERACOES_PAGAMENTO_FATURAMENTO}
                  capturarDetalhesPagamento
                  habilitarAtalhosTeclado
                  capturarDataCompensacaoBoleto
                />
              </Box>

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                  bgcolor: alpha(theme.palette.primary.main, 0.02),
                  width: { xs: '100%', lg: 300 },
                  flexShrink: 0,
                }}
              >
                <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1.5 }}>
                  Resumo da venda
                </Typography>

                <Typography variant="caption" color="text.secondary">Cliente</Typography>
                <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5, lineHeight: 1.2 }}>
                  {props.parceiroNome}
                </Typography>
                {props.cpfCliente && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    CPF: {props.cpfCliente}
                  </Typography>
                )}
                {props.numeroVenda !== undefined && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    Nº da venda: {props.numeroVenda}
                  </Typography>
                )}

                <Divider sx={{ my: 1.5 }} />

                <Box display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Total a pagar</Typography>
                  <Typography variant="h6" fontWeight={800} color="primary.dark">
                    {formatarMoeda(props.valorTotal)}
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Pago</Typography>
                  <Typography variant="body1" fontWeight={700}>
                    {formatarMoeda(somaFormas)}
                  </Typography>
                </Box>

                <LinearProgress
                  variant="determinate"
                  value={percentualPago}
                  color={percentualPago >= 100 ? 'success' : 'primary'}
                  sx={{ height: 6, borderRadius: 3, mb: 0.5 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  {percentualPago.toFixed(0)}%
                </Typography>

                <Divider sx={{ my: 1.5 }} />

                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Restante
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight={700}
                    color={restante > 0.001 ? 'warning.main' : 'success.main'}
                  >
                    {formatarMoeda(restante)}
                  </Typography>
                </Box>
              </Paper>
            </Box>
          </VForm>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          {!showFullScreenLoading && (
            <>
              <Button
                variant='text'
                color='inherit'
                onClick={handleCancel}
              >
                Cancelar
              </Button>

              <Button
                variant='contained'
                startIcon={<Icon>check</Icon>}
                onClick={save}
                disabled={isLoading || requestInProgress.current}
              >
                {isLoading ? 'Faturando...' : 'Confirmar Faturamento (F12)'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
